'use client';

import React from 'react';
import { Check } from 'lucide-react';
import type { ColorTheme } from './EventSetupClient';

interface ColorThemePickerProps {
  value: ColorTheme;
  onChange: (theme: ColorTheme) => void;
}

const themes: Array<{
  id: ColorTheme;
  label: string;
  description: string;
  boyColor: string;
  girlColor: string;
  preview: string;
}> = [
  {
    id: 'classic',
    label: 'Clásico',
    description: 'Azul & Rosa tradicional',
    boyColor: 'bg-blue-400',
    girlColor: 'bg-pink-400',
    preview: 'from-blue-400 to-pink-400',
  },
  {
    id: 'pastel',
    label: 'Pastel',
    description: 'Tonos suaves y delicados',
    boyColor: 'bg-sky-300',
    girlColor: 'bg-rose-300',
    preview: 'from-sky-300 to-rose-300',
  },
  {
    id: 'bold',
    label: 'Intenso',
    description: 'Colores vibrantes y fuertes',
    boyColor: 'bg-blue-700',
    girlColor: 'bg-pink-700',
    preview: 'from-blue-700 to-pink-700',
  },
  {
    id: 'sunset',
    label: 'Atardecer',
    description: 'Naranja cálido & Rosa',
    boyColor: 'bg-orange-400',
    girlColor: 'bg-rose-400',
    preview: 'from-orange-400 to-rose-400',
  },
  {
    id: 'mint',
    label: 'Menta',
    description: 'Turquesa & Violeta',
    boyColor: 'bg-teal-400',
    girlColor: 'bg-violet-400',
    preview: 'from-teal-400 to-violet-400',
  },
  {
    id: 'olive-lilac',
    label: 'Oliva & Lila',
    description: 'Verde oliva & Lila elegante',
    boyColor: 'bg-[#6B7C3E]',
    girlColor: 'bg-[#B07FD4]',
    preview: 'from-[#6B7C3E] to-[#B07FD4]',
  },
  {
    id: 'golden-silver',
    label: 'Oro & Plata',
    description: 'Dorado & Plateado lujoso',
    boyColor: 'bg-[#C9A84C]',
    girlColor: 'bg-[#9BA8B5]',
    preview: 'from-[#C9A84C] to-[#9BA8B5]',
  },
];

export default function ColorThemePicker({ value, onChange }: ColorThemePickerProps) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-blue-400 flex items-center justify-center">
          <span className="text-xs">🎨</span>
        </div>
        <div>
          <h2 className="text-base font-700 text-foreground">Tema de Colores</h2>
          <p className="text-xs text-muted-foreground">Elige el estilo visual de tu evento</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
        {themes.map((theme) => (
          <button
            key={`theme-${theme.id}`}
            type="button"
            onClick={() => onChange(theme.id)}
            className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all duration-200 ${
              value === theme.id
                ? 'border-primary bg-primary/5 card-shadow-pink'
                : 'border-border bg-muted/30 hover:border-primary/40 hover:bg-muted/60'
            }`}
          >
            {/* Color preview */}
            <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${theme.preview} flex items-center justify-center`}>
              {value === theme.id && (
                <Check size={14} className="text-white font-700" strokeWidth={3} />
              )}
            </div>
            <span className="text-xs font-600 text-foreground text-center leading-tight">{theme.label}</span>
            {/* Boy/girl dots */}
            <div className="flex gap-1">
              <span className={`w-2 h-2 rounded-full ${theme.boyColor}`} />
              <span className={`w-2 h-2 rounded-full ${theme.girlColor}`} />
            </div>
          </button>
        ))}
      </div>

      {/* Selected theme description */}
      <div className="mt-3 p-2.5 rounded-xl bg-muted/40 border border-border">
        <p className="text-xs text-muted-foreground text-center">
          <span className="font-600 text-foreground">
            {themes.find((t) => t.id === value)?.label}:
          </span>{' '}
          {themes.find((t) => t.id === value)?.description}
        </p>
      </div>
    </div>
  );
}